/**
 * Created by Mike Naughton on 3/12/18.
 *
 * Provides some common functionality for classes that contain a `getLayers` function.
 */

import $ from "jquery";
import _ from "underscore";
import FieldCollection from "app/models/FieldCollection";
import async from "async";

export default {
  /**
   * Performs a depth-first walk of the entire layer tree inside this service.
   * @param  {Function} action The `function(layer, parents)` to execute with each layer. The "layer" argument will receive the present `MapLayerBase`, and the "parents" argument will receive the list of parent layers all the way to root, with the most immediate parent first in the array.
   * @param  {Boolean} [pre]    True to perform a pre-order traversal, otherwise, a post-order traversal will be done.
   */
  eachLayer: function (action, pre) {
    function visitLayer(layer, parents) {
      var childParents = parents.slice();

      childParents.unshift(layer);

      if (pre) {
        action(layer, parents);
      }

      if (layer.children) {
        _.each(layer.children, function (child) {
          visitLayer(child, childParents);
        });
      }

      if (!pre) {
        action(layer, parents);
      }
    }

    var parents = [];

    _.each(this.getLayers(), function (layer) {
      visitLayer(layer, parents);
    });
  },
  /**
   * Asynchronous equivalent of `eachLayer`.
   * @param  {Function}   action See `eachLayer` for first two arguments. Third argument is callback function, to be executed once the visit is complete. If an error occurs, it can be sent as the first argument.
   * @param  {Boolean}   pre    See `eachLayer`.
   * @param  {Function} cb     Function expecting a single argument, which is an error, if any, to be executed once the traversal is complete.
   */
  eachLayerAsync: function (action, pre, cb) {
    function visitLayer(layer, parents, cb) {
      var childParents = parents.slice();

      childParents.unshift(layer);
      function visitChildren(cb) {
        if (layer.children) {
          async.eachSeries(
            layer.children,
            function (layer, cb) {
              visitLayer(layer, childParents, cb);
            },
            cb
          );
        } else {
          cb();
        }
      }

      async.series(
        [
          function (cb) {
            if (pre) {
              action(layer, parents, cb);
            } else {
              cb();
            }
          },
          visitChildren,
        ],
        function (err) {
          if (err) {
            return cb(err);
          }

          if (!pre) {
            action(layer, parents, cb);
          } else {
            cb();
          }
        }
      );
    }

    var parents = [];

    async.eachSeries(
      this.getLayers(),
      function (layer, cb) {
        visitLayer(layer, parents, cb);
      },
      cb
    );
  },
  /**
   * Loads the collections of documents associated with each object in this layer.
   * @return {Promise} A promise object which shall resolve, if no error, with a mapping from layer ID to a mapping from model ID to `DocumentCollection`. If `hasFiles` returns false, this shall return null.
   */
  loadFiles: function () {
    if (!this.hasFiles()) {
      return null;
    }

    var deferred = new $.Deferred();
    var layerMap = {};

    this.eachLayerAsync(
      function (layer, parents, cb) {
        layer
          .loadFiles()
          .done(function (_layerMap) {
            _.each(_.keys(_layerMap), function (layerId) {
              layerMap[layerId] = _layerMap[layerId];
            });
            cb();
          })
          .fail(function (err) {
            cb(err);
          });
      },
      false,
      function (err) {
        if (err) {
          return deferred.reject(err);
        }

        deferred.resolve(layerMap);
      }
    );

    return deferred.promise();
  },

  /**
   * Gets a value indicating whether or not any layers in this service contain files.
   * @return {Boolean} True if there are files, otherwise false.
   */
  hasFiles: function () {
    var hasFiles = false;

    this.eachLayer(function (layer) {
      hasFiles = hasFiles || layer.hasFiles();
    });

    return hasFiles;
  },

  /**
   * Gets the set of all distinctly-named fields across all layers in this service. The value is not cached, so each invocation will result in an iteration across all layers.
   * @param {Boolean} [onlyShared] True to retrieve only those fields that are shared across all layers, false otherwise.
   * @return {Array} Returns an array of `Field` instances.
   */
  getFields: function (onlyShared) {
    var layers = [];

    this.eachLayer(function (layer) {
      layers.push(layer);
    });

    return FieldCollection.getFields(layers, onlyShared);
  },
};



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/map/base/LayerContainer.mixin.js
// module id = 355
// module chunks = 1